import React, { useEffect, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";

/** Above this many people, Send stops to say out loud who is about to be contacted. */
export const CONFIRM_THRESHOLD = 25;

export const needsConfirm = (total) => Number(total || 0) > CONFIRM_THRESHOLD;

/**
 * The last thing between a manager and 264 emails.
 *
 * It does not ask "are you sure" — that question teaches people to click through. It states the
 * facts that would make somebody stop: how many people, how many units, which building, and on
 * which channels.
 */
const SendConfirm = ({ total, units, where, channels, companyWide, propertyCount, scheduled, recurring = false, scheduleSummary = "", onConfirm, onCancel, busy }) => (
  <div className="rn-confirm">
    <p className="rn-confirm-eyebrow">Before this goes out</p>
    <p className="rn-confirm-line">
      <strong>{total} residents</strong> across <strong>{units} units</strong>
    </p>
    <p className="rn-confirm-where">{where}</p>
    <p className="rn-confirm-ch">
      <FeatherIcon icon="send" size={13} /> {channels}
    </p>

    {companyWide ? (
      <p className="rn-confirm-warn">
        <FeatherIcon icon="alert-triangle" size={14} /> This reaches every resident at {propertyCount || "all"} properties, not
        just this building.
      </p>
    ) : null}

    {/* A scheduled notice has no undo window - it simply waits, and Cancel schedule is the
        way back. Promising 30 seconds there would be a promise the server does not keep. */}
    <p className="rn-confirm-note">
      {recurring
        ? `${scheduleSummary}. You can cancel the next scheduled reminder to stop the series.`
        : scheduled
        ? "Nothing goes out until the time you picked. You can cancel the schedule until then."
        : "You will have 30 seconds to stop it afterwards."}
    </p>

    <div className="rn-confirm-actions">
      <button type="button" className="rn-btn" onClick={onCancel} disabled={busy}>
        Back
      </button>
      <button type="button" className="rn-btn rn-btn-primary" onClick={onConfirm} disabled={busy}>
        {recurring ? "Schedule for" : "Send to"} <span className="rn-confirm-n">{total}</span> resident{total === 1 ? "" : "s"}
      </button>
    </div>
  </div>
);

/**
 * The thirty seconds after Send. Nothing has left yet; the server is holding the notice and
 * the timer here only mirrors that. `onDone` fires when the window shuts so the modal can move
 * on without waiting for the send itself.
 *
 * The callback is held in a ref so a parent that re-renders (or hands over a fresh arrow every
 * render) cannot restart the tick, and the timeout is cleared on unmount so a closed modal
 * never keeps counting.
 */
export const UndoBar = ({ seconds = 30, deadline, busy = false, onUndo, onDone }) => {
  const [left, setLeft] = useState(() => {
    const end = Date.parse(deadline);
    return Number.isFinite(end) ? Math.max(0, Math.ceil((end - Date.now()) / 1000)) : seconds;
  });
  const doneRef = useRef(onDone);
  const busyRef = useRef(busy);
  doneRef.current = onDone;
  busyRef.current = busy;

  useEffect(() => {
    const serverEnd = Date.parse(deadline);
    const end = Number.isFinite(serverEnd) ? serverEnd : Date.now() + seconds * 1000;
    let finished = false;
    let timer;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      setLeft(remaining);
      if (!remaining && !finished && !busyRef.current) {
        finished = true;
        clearInterval(timer);
        doneRef.current?.();
      }
    };
    timer = setInterval(tick, 250);
    tick();
    return () => clearInterval(timer);
  }, [deadline, seconds]);

  return (
    <div className="rn-undo">
      <span className="rn-undo-count" role="timer" aria-label={`${left} seconds remaining`} aria-live="off">{left}s</span>
      <div className="rn-undo-copy">
        <span className="rn-undo-title">{left > 0 ? 'Notice queued for delivery' : busy ? 'Stopping your notice' : 'Delivery is starting'}</span>
        <span className="rn-undo-text">{left > 0 ? 'Nothing has been sent. Use Undo to return to your draft.' : 'The undo window has ended.'}</span>
        <progress className="rn-undo-progress" max={Math.max(seconds, left, 1)} value={left} aria-label="Time remaining to undo" />
      </div>
      <button type="button" className="rn-btn rn-undo-button" onClick={onUndo} disabled={busy || left <= 0}>
        <FeatherIcon icon="rotate-ccw" size={15} /> {busy ? 'Stopping...' : 'Undo'}
      </button>
    </div>
  );
};

export default SendConfirm;
